/**
 * All of a vendor's codes as one display string, primary first.
 * Falls back to the legacy single vendor_code field; '' when uncoded.
 */
export const joinVendorCodes = (vendor) => {
  const codes = Array.isArray(vendor?.codes) && vendor.codes.length > 0
    ? vendor.codes.map((c) => c.code)
    : (vendor?.vendor_code ? [vendor.vendor_code] : []);
  return codes.join(', ');
};

/**
 * How the Vendor Master should label a vendor that has no code yet.
 *
 * "Pending" alone was uninformative — a vendor awaiting a human decision, one that is an
 * unfixable duplicate, and one the ERP has never heard of all looked identical. The
 * backend supplies `code_status` (+ `code_status_detail`); this maps it to a label.
 */
export const CODE_STATUS_LABELS = {
  // Has a code — the settled, happy state. Green so a coded row reads as "done"
  // at a glance next to the code itself.
  ASSIGNED: { label: 'Assigned', dot: 'bg-green-500', text: 'text-green-700' },
  // Needs a human. Warm colours so they stand out from the merely-waiting rows.
  CONFLICT: { label: 'Conflict', dot: 'bg-amber-500', text: 'text-amber-700' },
  DUPLICATE: { label: 'Duplicate', dot: 'bg-orange-500', text: 'text-orange-700' },
  DISMISSED: { label: 'Assign manually', dot: 'bg-purple-500', text: 'text-purple-700' },
  NO_NAME: { label: 'No company name', dot: 'bg-red-500', text: 'text-red-700' },
  // Retries or resolves on its own — kept muted so it does not read as a problem.
  SYNC_ERROR: { label: 'Retrying', dot: 'bg-yellow-400', text: 'text-yellow-700' },
  // "Unmatched" mirrors the word the ERP-sync panel and Code Conflicts screen use
  // for this exact state; the old "Awaiting ERP" read like "not yet scanned" (which
  // is PENDING) and made the status look like a placeholder.
  NOT_IN_ERP: { label: 'Unmatched', dot: 'bg-slate-400', text: 'text-slate-500' },
  PENDING: { label: 'Not synced', dot: 'bg-slate-300', text: 'text-slate-500' },
  // Terminal — no action possible.
  REJECTED: { label: 'Rejected', dot: 'bg-slate-400', text: 'text-slate-400' },
};

export const getCodeStatus = (vendor) =>
  CODE_STATUS_LABELS[vendor?.code_status] || CODE_STATUS_LABELS.PENDING;

/** Same, but tags each code with its state when known: "VA1 (UP), VA2 (UK)" */
export const joinVendorCodesWithStates = (vendor) => {
  if (!Array.isArray(vendor?.codes) || vendor.codes.length === 0) return joinVendorCodes(vendor);
  return vendor.codes.map((c) => (c.state ? `${c.code} (${c.state})` : c.code)).join(', ');
};
